import { gql } from '@apollo/client';

import { getMemberId } from '@trello/authentication';
import { client } from '@trello/graphql';
import type { PIIString } from '@trello/privacy';
import { cacheFactory, preloadErrorMessage } from '@trello/quickload';

import type { UsernameContext } from './context';

const MEMBER_FRAGMENT = gql`
  fragment MemberUsername on Member {
    id
    username
  }
`;

const MEMBER_QUERY = gql`
  query MemberUsername {
    member(id: "me") @client {
      id
      username
    }
  }
`;

/**
 * Get the current user's username from preloads or Apollo cache
 * @returns The username or null if not found in cache
 */
export const getCurrentUsername = async (): Promise<UsernameContext> => {
  let username: PIIString | undefined = undefined;
  let memberId = getMemberId() ?? undefined;

  // Let's wait on the cache to hydrate from quickload
  try {
    await cacheFactory.waitForQueryHydratedTo('MemberHeader', 'Apollo');

    const cachedMember = client.readFragment<{
      id: string;
      username: PIIString;
    }>({
      id: `Member:${memberId}`,
      fragment: MEMBER_FRAGMENT,
    });

    username = cachedMember?.username;
  } catch (error) {
    // This is an "error" we expect to happen once the quickload cache has been cleared, so only log unexpected errors
    if (error !== preloadErrorMessage) {
      console.error('Error fetching current username from cache:', error);
    }
  }

  // Ideally this is already in the cache from quickload preloads, but as a fallback we can make a cache-first query
  if (!username) {
    const queriedMember = await client.query<{
      member: { id: string; username: PIIString };
    }>({
      fetchPolicy: 'cache-first',
      query: MEMBER_QUERY,
    });
    memberId = queriedMember.data.member?.id;
    username = queriedMember.data.member?.username;
  }

  return { memberId, username };
};
